import { useState, useEffect, useCallback } from 'react';
import { crmService } from '../../services/crmService';
import InteractionModal from './InteractionModal';

// ─── Constants ─────────────────────────────────────────────────────────────────

const TYPE_ICONS = {
  meeting:            '🤝',
  phone_call:         '📞',
  email:              '📧',
  video_call:         '🖥',
  conference:         '🎪',
  site_visit:         '📍',
  demo:               '🎯',
  proposal_delivery:  '📋',
  social:             '🍽',
  tradeshow:          '🏢',
  briefing:           '📊',
  congressional_visit:'🏛',
};

const SENTIMENT_META = {
  positive: { color: '#10B981', label: 'Positive' },
  neutral:  { color: '#6B7280', label: 'Neutral'  },
  negative: { color: '#EF4444', label: 'Negative' },
  mixed:    { color: '#3B82F6', label: 'Mixed'    },
};

const STAGE_COLORS = {
  identify:  '#6B7280', qualify:   '#1D4ED8', develop:   '#8B5CF6',
  capture:   '#F59E0B', propose:   '#EA580C', negotiate: '#EF4444',
  won:       '#10B981', lost:      '#374151',
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtValue(v) {
  if (!v) return null;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v}`;
}

function authorityColor(score) {
  if (score >= 80) return '#22D3EE';
  if (score >= 60) return '#3B82F6';
  return '#64748B';
}

// ─── Timeline item ─────────────────────────────────────────────────────────────

function InteractionItem({ item, onDelete }) {
  const [expanded, setExpanded]   = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const icon    = TYPE_ICONS[item.type] || '📌';
  const senti   = SENTIMENT_META[item.sentiment];
  const hasMore = (item.summary || '').length > 100;

  return (
    <div style={{
      background: '#0A0F1C', borderRadius: 8,
      border: '1px solid #1E2D45', padding: '12px 14px',
      display: 'flex', gap: 12,
    }}>
      {/* Icon column */}
      <div style={{ flexShrink: 0, fontSize: 20, lineHeight: 1, paddingTop: 2 }}>{icon}</div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Top row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ color: '#F1F5F9', fontWeight: 600, fontSize: 13 }}>{item.subject || '(no subject)'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {senti && (
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: senti.color, display: 'inline-block', flexShrink: 0,
              }} title={senti.label} />
            )}
            <span style={{ color: '#64748B', fontSize: 11, whiteSpace: 'nowrap' }}>{fmtDate(item.interaction_date)}</span>
          </div>
        </div>

        {/* Summary */}
        {item.summary && (
          <div
            onClick={() => hasMore && setExpanded(e => !e)}
            style={{
              color: '#94A3B8', fontSize: 12, lineHeight: 1.6, marginBottom: 6,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: expanded ? 'unset' : 2,
              WebkitBoxOrient: 'vertical',
              cursor: hasMore ? 'pointer' : 'default',
            }}
          >
            {item.summary}
          </div>
        )}
        {hasMore && (
          <button
            onClick={() => setExpanded(e => !e)}
            style={{ background: 'none', border: 'none', color: '#22D3EE', fontSize: 11, cursor: 'pointer', padding: 0, marginBottom: 6 }}
          >
            {expanded ? 'Show less' : 'Read more'}
          </button>
        )}

        {/* Next action */}
        {(item.next_steps || item.next_action_date) && (
          <div style={{ color: '#F59E0B', fontSize: 11, marginTop: 4 }}>
            →{item.next_steps ? ` ${item.next_steps}` : ''}{item.next_action_date ? ` by ${fmtDate(item.next_action_date)}` : ''}
          </div>
        )}

        {/* Footer row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
          <span style={{ color: '#374151', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {item.type?.replace(/_/g, ' ')}
            {item.opportunity_name && ` · ${item.opportunity_name}`}
          </span>
          <button
            onClick={() => {
              if (!confirmDel) { setConfirmDel(true); return; }
              onDelete(item.id);
            }}
            onBlur={() => setTimeout(() => setConfirmDel(false), 200)}
            style={{
              background: confirmDel ? '#EF444422' : 'transparent',
              color: confirmDel ? '#EF4444' : '#374151',
              border: `1px solid ${confirmDel ? '#EF4444' : 'transparent'}`,
              borderRadius: 4, padding: '2px 7px', cursor: 'pointer', fontSize: 10,
            }}
          >
            {confirmDel ? 'Delete?' : '✕'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Timeline tab ──────────────────────────────────────────────────────────────

function TimelineTab({ contactId, refresh }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await crmService.getInteractions({ contact_id: contactId, days: 36500 });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [contactId]);

  useEffect(() => { load(); }, [load]);
  // refresh counter triggers a reload
  useEffect(() => { if (refresh > 0) load(); }, [refresh, load]);

  const handleDelete = async (id) => {
    try {
      await crmService.deleteInteraction(id);
      load();
    } catch (e) { alert(e.message); }
  };

  if (loading) return <div style={{ color: '#64748B', padding: '24px 0', textAlign: 'center' }}>Loading…</div>;

  if (items.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <img src="/elephant-icon.svg" alt="" style={{ width: 56, height: 56, opacity: 0.2, marginBottom: 16 }} />
        <div style={{ color: '#94A3B8', fontSize: 14, marginBottom: 4 }}>No interactions yet</div>
        <div style={{ color: '#4B5563', fontSize: 12 }}>Log your first one using the button above.</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map(item => (
        <InteractionItem key={item.id} item={item} onDelete={handleDelete} />
      ))}
    </div>
  );
}

// ─── Opportunities tab ─────────────────────────────────────────────────────────

function OpportunitiesTab({ opportunities }) {
  if (!opportunities || opportunities.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0', color: '#4B5563', fontSize: 13 }}>
        No linked opportunities.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {opportunities.map(op => {
        const stageColor = STAGE_COLORS[op.stage] || '#374151';
        return (
          <div key={op.id} style={{
            background: '#0A0F1C', border: '1px solid #1E2D45',
            borderLeft: `3px solid ${stageColor}`,
            borderRadius: 8, padding: '12px 14px',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
              <div style={{ color: '#F1F5F9', fontWeight: 600, fontSize: 13, flex: 1, minWidth: 0 }}>{op.name}</div>
              <span style={{
                background: stageColor + '22', color: stageColor,
                border: `1px solid ${stageColor}44`,
                borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700,
                whiteSpace: 'nowrap', flexShrink: 0, textTransform: 'capitalize',
              }}>
                {op.stage}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {op.value_usd && (
                <span style={{ color: '#22D3EE', fontSize: 12, fontWeight: 600 }}>{fmtValue(op.value_usd)}</span>
              )}
              {op.subsidiary_name && (
                <span style={{ color: '#64748B', fontSize: 11 }}>{op.subsidiary_name}</span>
              )}
              {op.role && (
                <span style={{ color: '#94A3B8', fontSize: 11, textTransform: 'capitalize' }}>
                  {op.role?.replace(/_/g, ' ')}
                </span>
              )}
              {op.award_date && (
                <span style={{ color: '#4B5563', fontSize: 11 }}>Award: {fmtDate(op.award_date)}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────────

export default function ContactDetail({ contact, onClose, onEdit }) {
  const [tab, setTab]             = useState('timeline');
  const [fullContact, setFull]    = useState(null);
  const [loadingFull, setLF]      = useState(true);
  const [showLogModal, setShowLog] = useState(false);
  const [refreshTick, setRefresh] = useState(0);
  const [visible, setVisible]     = useState(false);

  // Slide-in animation after mount
  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Fetch full contact on open
  useEffect(() => {
    setLF(true);
    crmService.getContact(contact.id)
      .then(setFull)
      .catch(console.error)
      .finally(() => setLF(false));
  }, [contact.id]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const c           = fullContact || contact;
  const displayName = `${c.rank_code || c.grade_code ? (c.rank_code || c.grade_code) + ' ' : ''}${c.first_name} ${c.last_name}`;
  const score       = c.authority_score;
  const barColor    = authorityColor(score);

  const handleLogSuccess = () => {
    setShowLog(false);
    setRefresh(r => r + 1);
  };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          zIndex: 999, cursor: 'pointer',
          opacity: visible ? 1 : 0, transition: 'opacity 0.25s',
        }}
      />

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, height: '100vh', width: 'clamp(280px, 60vw, 480px)',
        background: '#111827', borderLeft: '1px solid #1E2D45',
        zIndex: 1000, display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '-12px 0 40px rgba(0,0,0,0.6)',
        transform: visible ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.25s cubic-bezier(0.4,0,0.2,1)',
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 20px 14px', borderBottom: '1px solid #1E2D45',
          flexShrink: 0, background: '#0D1F3C',
        }}>
          {/* Top row: name + close */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: '#F1F5F9', fontWeight: 700, fontSize: 17, lineHeight: 1.3 }}>
                {displayName}
              </div>
              {c.org_name && (
                <div style={{ color: '#64748B', fontSize: 12, marginTop: 3 }}>
                  {c.org_acronym || c.org_name}
                  {c.title && ` · ${c.title}`}
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', color: '#64748B', cursor: 'pointer', fontSize: 22, lineHeight: 1, flexShrink: 0, marginLeft: 8 }}
            >
              ×
            </button>
          </div>

          {/* Rank/grade + authority */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            {(c.rank_code || c.grade_code) && (
              <span style={{
                background: '#22D3EE22', color: '#22D3EE', border: '1px solid #22D3EE44',
                borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700,
              }}>
                {c.rank_code || c.grade_code}
              </span>
            )}
            {score != null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
                <div style={{ flex: 1, height: 5, background: '#1E2D45', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ width: `${score}%`, height: '100%', background: barColor, borderRadius: 3 }} />
                </div>
                <span style={{ color: barColor, fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                  {score}
                </span>
              </div>
            )}
          </div>

          {/* Contact links */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14, fontSize: 12 }}>
            {c.email && (
              <a href={`mailto:${c.email}`} style={{ color: '#94A3B8', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                <span>✉</span> {c.email}
              </a>
            )}
            {c.phone && (
              <a href={`tel:${c.phone}`} style={{ color: '#94A3B8', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                <span>📞</span> {c.phone}
              </a>
            )}
            {c.linkedin_url && (
              <a href={c.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ color: '#3B82F6', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                <span>🔗</span> LinkedIn
              </a>
            )}
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setShowLog(true)}
              style={{
                flex: 1, background: '#22D3EE', color: '#0A0F1C', border: 'none',
                borderRadius: 6, padding: '8px 0', cursor: 'pointer', fontSize: 13, fontWeight: 700,
              }}
            >
              + Log Interaction
            </button>
            {onEdit && (
              <button
                onClick={() => onEdit(c)}
                style={{
                  background: 'transparent', color: '#94A3B8',
                  border: '1px solid #374151', borderRadius: 6,
                  padding: '8px 16px', cursor: 'pointer', fontSize: 13,
                }}
              >
                Edit Contact
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #1E2D45', flexShrink: 0 }}>
          {[['timeline', 'Timeline'], ['opportunities', 'Opportunities']].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              style={{
                padding: '10px 20px', background: 'none', border: 'none',
                borderBottom: tab === k ? '2px solid #22D3EE' : '2px solid transparent',
                color: tab === k ? '#22D3EE' : '#6B7280',
                cursor: 'pointer', fontSize: 13, fontWeight: tab === k ? 600 : 400,
                marginBottom: -1,
              }}
            >
              {label}
              {k === 'opportunities' && !loadingFull && (c.opportunities?.length > 0) && (
                <span style={{
                  marginLeft: 6, background: '#22D3EE22', color: '#22D3EE',
                  borderRadius: 10, padding: '0 6px', fontSize: 10, fontWeight: 700,
                }}>
                  {c.opportunities.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {tab === 'timeline' && (
            <TimelineTab contactId={contact.id} refresh={refreshTick} />
          )}
          {tab === 'opportunities' && (
            <OpportunitiesTab opportunities={c.opportunities} />
          )}
        </div>
      </div>

      {/* Interaction log modal */}
      {showLogModal && (
        <InteractionModal
          contact={c}
          onClose={() => setShowLog(false)}
          onSuccess={handleLogSuccess}
        />
      )}
    </>
  );
}
